import { mdiAlert, mdiAt, mdiCheckCircle, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { serverTimestamp } from 'firebase/firestore';
import { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import PageLoading from '@/components/General/PageLoading';
import useUsernameInput from '@/hooks/usernameInputHook';
import * as gtag from '@/lib/gtag';
import { useAuth } from '@/providers/AuthProvider';
import { updateUser } from '@/providers/AuthProvider/hooks/useUser';
import { IUser } from '@/types/Collections/users';
import { FirebaseError, IAuthError } from '@/providers/AuthProvider/types';
import AuthErrorMessage from '@/components/Auth/AuthErrorMessage';
import eventAdd from '@/lib/eventAdd';

const UsernamePage: NextPage = () => {
  const { authLoaded, isLoggedIn, uid, user, auth } = useAuth();
  const router = useRouter();

  const [error, setError] = useState<IAuthError | FirebaseError | null>(null);

  const {
    username,
    isAvailable: usernameIsAvailable,
    isChecking: usernameIsChecking,
    isValid: usernameIsValid,
    bind: bindUsername,
    touched: userNameTouched,
  } = useUsernameInput('');

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (authLoaded && (!isLoggedIn || user?.username)) {
      router.push('/home');
    }
  }, [authLoaded, isLoggedIn, router, user?.username]);

  const save = async (profile: Partial<IUser>) => {
    if (uid) {
      await updateUser(uid, {
        ...profile,
        isActivated: true,
        isDeleted: false,
        created: serverTimestamp(),
      }).then(result => {
        if (result) {
          if (result.success) {
            if (uid && auth?.email) {
              gtag.event({
                action: 'profile.username',
                params: {
                  name: 'Set username',
                  uid,
                },
              });
              eventAdd({
                uid,
                eventType: 'USER:SET_USERNAME',
                eventSource: 'USER',
                eventSourceId: uid,
                meta: {
                  username: profile.username,
                },
                email: auth.email,
                platform: 'web',
              });
            }
            router.push('/welcome');
          } else {
            setError(result.error);
          }
        }
      });
    }
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!usernameIsValid) {
      return false;
    }
    if (!usernameIsAvailable) {
      return false;
    }
    setIsLoading(true);
    const profile = {
      username,
      usernameLowercase: username.toLowerCase(),
    };
    save(profile);
    return true;
  };

  if (!authLoaded) {
    return <PageLoading />;
  }

  if (authLoaded && !isLoggedIn) {
    return (
      <div className="hero is-fullheight">
        <div className="hero-body">
          <div className="container is-narrow has-text-centered">
            <div>
              <Icon path={mdiAlert} size={3} />
            </div>
            <p className="is-font-brand is-size-3 mb-4">
              Sorry, you have been logged out
            </p>
            <p className="is-font-brand mb-4">
              This usually happens if time has elapsed between you submitting
              this page and logging in. Please try logging in again.
            </p>
            <div>
              <Link href="/signin" legacyBehavior>
                <a className="button is-primary is-rounded">
                  <span className="icon">
                    <Icon path={mdiOpenInNew} size={1} />
                  </span>
                  <span>Login</span>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="username-page">
      <Head>
        <title>Welcome to Shift.ms</title>
      </Head>
      <div className="container is-narrow">
        <div className="username-box">
          <div className="content">
            <div className="join-form is-clearfix">
              <AuthErrorMessage error={error} />
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label
                    htmlFor="username"
                    className="label title is-size-3 is-size-5-mobile">
                    Please choose a username
                  </label>
                  <div
                    className={`control has-icons-left has-icons-right${
                      usernameIsChecking ? ' is-loading' : ''
                    }`}>
                    <input
                      id="username"
                      className="input"
                      type="text"
                      placeholder="Enter a username"
                      {...bindUsername}
                    />
                    <span className="icon is-small is-left">
                      <Icon path={mdiAt} size={1} />
                    </span>
                    {!usernameIsChecking &&
                    usernameIsValid &&
                    usernameIsAvailable ? (
                      <span className="icon is-small is-right has-text-success">
                        <Icon path={mdiCheckCircle} size={1} />
                      </span>
                    ) : null}
                  </div>
                  <p className="help">
                    You can use any combination of letters and numbers, but not
                    solely numbers. Minimum four characters, maximum 15
                    characters.
                  </p>

                  {!usernameIsChecking &&
                  username !== user?.username &&
                  usernameIsValid &&
                  usernameIsAvailable ? (
                    <p className="help is-alt-green">
                      Great! This username is available.
                    </p>
                  ) : (
                    ''
                  )}
                  {userNameTouched &&
                  !usernameIsChecking &&
                  usernameIsValid &&
                  !usernameIsAvailable ? (
                    <p className="help is-danger">
                      Sorry, this username is not available.
                    </p>
                  ) : (
                    ''
                  )}
                  {userNameTouched && !usernameIsValid ? (
                    <p className="help is-danger">
                      Please ensure your username has a minimum of four
                      characters, a maxmimum of 15 characters and only use
                      letters and numbers.
                    </p>
                  ) : (
                    ''
                  )}
                </div>

                <div className="form-buttons">
                  <div className="field submit-field">
                    <div className="control">
                      {!usernameIsChecking &&
                      usernameIsValid &&
                      usernameIsAvailable ? (
                        <button
                          type="submit"
                          className={`button is-rounded is-medium is-fullwidth is-primary${
                            isLoading ? ' is-loading' : ''
                          }`}
                          disabled={false}>
                          Complete signup
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="button is-rounded is-medium is-fullwidth is-primary"
                          disabled>
                          Complete signup
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsernamePage;
